import React, { useCallback, useState } from 'react';

import { check, upgrade } from '@/assets/icons';
import { LinkFrom } from '@/config/constants';
import {
	Contributor,
	useUpsertVideoCollectionStatMutation,
	Video,
	VideoChat,
	VideoCollection,
	VideoCollectionStat,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { getMinutesFromSeconds, timeFormatter } from '@/helpers/timeFormatter';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { SvgIcon, useMediaQuery } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';
import { useNavigate } from 'react-router';

import { ActionSheet, ActionSheetAction } from '../shared/ActionSheet';
import { AsaphButton } from '../shared/Button';
import LinearProgressIndicator from '../shared/ContentCard/LinearProgressIndicator';
import styles from './VideoCollectionRow.module.scss';

type LimitedContributor = Pick<Contributor, 'name' | 'imageUrl'>;
type LimitedVideoChat = Pick<VideoChat, 'id'>;
type LimitedVideo = Pick<
	Video,
	'id' | 'thumbnailUrl' | 'duration' | 'name' | 'stat' | 'paid'
> & {
	contributor?: LimitedContributor | null;
	chat?: LimitedVideoChat | null;
};

type LimitedVideoCollection = Pick<
	VideoCollection,
	'id' | 'name' | 'hex' | 'stat'
> & {
	videos?: LimitedVideo[] | null;
};

export type VideoCollectionRowProps = {
	className?: string;
	videoCollection: LimitedVideoCollection;
	linkFrom?: LinkFrom;
};

export const VideoCollectionRow: React.FC<VideoCollectionRowProps> = ({
	className,
	videoCollection,
	linkFrom,
}) => {
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);
	const { enqueueSnackbar } = useSnackbar();
	const { mutateAsync: upsertVideoCollectionStatMutate } =
		useUpsertVideoCollectionStatMutation();
	const queryClient = useQueryClient();
	const { status } = useSubscription();
	const { videoChatIds } = useUnreadChats();

	const [loading, setLoading] = useState(false);
	const [done, setDone] = useState(videoCollection.stat?.completed);
	const [activeActionSheetShown, setActiveActionSheetShown] = useState(false);
	const [completedActionSheetShown, setCompletedActionSheetShown] =
		useState(false);

	const showActiveActionSheet = useCallback(
		() => setActiveActionSheetShown(true),
		[setActiveActionSheetShown],
	);
	const closeActiveActionSheet = useCallback(
		() => setActiveActionSheetShown(false),
		[setActiveActionSheetShown],
	);

	const showCompletedActionSheet = useCallback(
		() => setCompletedActionSheetShown(true),
		[setCompletedActionSheetShown],
	);
	const closeCompletedActionSheet = useCallback(
		() => setCompletedActionSheetShown(false),
		[setCompletedActionSheetShown],
	);

	const markAsDone = useCallback(async () => {
		if (videoCollection?.stat == null) {
			return;
		}

		setLoading(true);
		try {
			await upsertVideoCollectionStatMutate({
				videoCollectionId: videoCollection.id,
				input: {
					completed: true,
				},
			});
			setDone(true);
			queryClient.invalidateQueries({
				queryKey: ['GetDashboard'],
			});
		} catch (e) {
			enqueueSnackbar('Something went wrong while updating this series', {
				variant: 'error',
			});
			return;
		} finally {
			setLoading(false);
			closeActiveActionSheet();
		}
	}, [
		upsertVideoCollectionStatMutate,
		videoCollection.id,
		videoCollection.stat,
		enqueueSnackbar,
		closeActiveActionSheet,
		queryClient,
	]);
	const markAsUndone = useCallback(async () => {
		if (videoCollection?.stat == null) {
			return;
		}

		setLoading(true);
		try {
			await upsertVideoCollectionStatMutate({
				videoCollectionId: videoCollection.id,
				input: {
					completed: false,
				},
			});
			setDone(false);
			queryClient.invalidateQueries({
				queryKey: ['GetDashboard'],
			});
		} catch (e) {
			enqueueSnackbar('Something went wrong while updating this series', {
				variant: 'error',
			});
			return;
		} finally {
			setLoading(false);
			closeCompletedActionSheet();
		}
	}, [
		upsertVideoCollectionStatMutate,
		videoCollection.id,
		videoCollection.stat,
		enqueueSnackbar,
		closeCompletedActionSheet,
		queryClient,
	]);

	const classes = classNames([
		styles['root'],
		isTablet && styles['scroll-padding'],
		className,
	]);
	const contributor = videoCollection.videos?.find(
		(v) => !!v.contributor,
	)?.contributor;
	const totalDuration = videoCollection.videos?.reduce<number>(
		(acc, video) => acc + video.duration,
		0,
	);

	return (
		<>
			<div className={classes}>
				<div
					className={styles['main-card']}
					style={{
						backgroundColor: videoCollection.hex || '#272e3d',
					}}
				>
					<div className={styles['title']}>
						{videoCollection.name}
					</div>
					<div className={styles['subtitle-row']}>
						<div className={styles['episodes']}>
							{videoCollection.videos
								? `${videoCollection.videos.length} episodes`
								: `Loading ...`}
						</div>
						<div className={styles['vertical-divider']} />
						<div className={styles['minutes']}>
							{getMinutesFromSeconds(totalDuration || 0)} minutes
						</div>
					</div>
					{!!contributor && (
						<div className={styles['contributor-row']}>
							<div
								className={styles['image']}
								style={{
									backgroundImage: `url(${contributor.imageUrl})`,
								}}
							/>
							<div
								className={classNames([
									styles['name'],
									styles['lower-title'],
								])}
							>
								{contributor.name}
							</div>
						</div>
					)}
				</div>

				{(videoCollection.videos || []).map((video) => (
					<VideoCard
						video={video}
						subscribed={false}
						linkFrom={linkFrom}
						key={`video-${video.id}-${videoCollection.id}`}
						hasNotification={
							!!video.chat?.id &&
							videoChatIds.includes(video.chat.id)
						}
					/>
				))}

				{done ? (
					<ActionCard
						isActive={true}
						title="Series complete!"
						blurb="Mark as not done"
						onActionClick={showCompletedActionSheet}
					/>
				) : videoCollection.stat?.canBeCompleted ? (
					<ActionCard
						isActive={true}
						title="Nice one! Series complete."
						blurb="Mark as done"
						onActionClick={showActiveActionSheet}
					/>
				) : (
					<ActionCard
						title="Watch all videos to mark as done."
						blurb="I'm done anyway"
						onActionClick={showActiveActionSheet}
					/>
				)}
			</div>

			<ActionSheet
				open={activeActionSheetShown}
				title="Progress is tracked based on video completion. Are you sure you want to mark this series as done?"
				onClose={closeActiveActionSheet}
			>
				<ActionSheetAction
					label="I'm sure, mark as done"
					onActionClick={markAsDone}
					clickContext={{
						handleDialogClose: closeActiveActionSheet,
					}}
					loading={loading}
				/>
			</ActionSheet>

			<ActionSheet
				open={completedActionSheetShown}
				title="Continue this series?"
				onClose={closeCompletedActionSheet}
			>
				<ActionSheetAction
					label="Mark undone"
					onActionClick={markAsUndone}
					clickContext={{
						handleDialogClose: closeCompletedActionSheet,
					}}
					loading={loading}
				/>
			</ActionSheet>
		</>
	);
};

const ActionCard: React.FC<{
	isActive?: boolean;
	onActionClick: () => void;
	title: string;
	blurb: string;
}> = ({ isActive, onActionClick, title, blurb }) => {
	return (
		<div
			className={classNames([
				styles['action-card'],
				isActive && styles['active'],
			])}
		>
			<div className={styles['lower-title']}>{title}</div>
			<div className={styles['icon']}>
				<SvgIcon component={check} />
			</div>
			<div
				className={classNames([styles['action'], styles['title']])}
				onClick={onActionClick}
			>
				{blurb}
			</div>
		</div>
	);
};

const VideoCard: React.FC<{
	isPaidContent?: boolean;
	video: LimitedVideo;
	subscribed?: boolean;
	linkFrom?: LinkFrom;
	hasNotification?: boolean;
}> = ({ isPaidContent, video, subscribed, linkFrom, hasNotification }) => {
	const navigate = useNavigate();

	let percentagePlayedComponent;
	if (!!video.stat?.percentagePlayed && video.stat.percentagePlayed < 1) {
		percentagePlayedComponent = (
			<LinearProgressIndicator
				percentagePlayed={video.stat.percentagePlayed * 100}
			/>
		);
	}
	if (!!video.stat?.fullyWatched) {
		percentagePlayedComponent = (
			<>
				<div className={styles['icon-container']}>
					<SvgIcon
						component={check}
						color="primary"
						classes={{ colorPrimary: styles['icon'] }}
					/>
				</div>
				{percentagePlayedComponent}
			</>
		);
	}
	if (isPaidContent) {
		percentagePlayedComponent = null;
	}

	return (
		<div
			onClick={() =>
				navigate(`/videos/${video.id}`, {
					state: {
						linkFrom: linkFrom || LinkFrom.dashboard,
					},
				})
			}
			className={styles['video-card']}
			style={{
				backgroundImage: `url('${video.thumbnailUrl}')`,
			}}
		>
			<div
				className={classNames([
					styles['overlay'],
					video.paid && !subscribed && styles['paid'],
				])}
			>
				<div className={styles['top']}>
					<div className={styles['left']}>
						{video.paid && !subscribed ? (
							<AsaphButton
								color="blue"
								label="Upgrade"
								icon={
									<SvgIcon
										component={upgrade}
										color="disabled"
										fontSize="inherit"
									/>
								}
								className={styles['upgrade-button']}
							/>
						) : (
							percentagePlayedComponent
						)}
					</div>

					<div className={styles['duration']}>
						{timeFormatter(video.duration)}
					</div>
				</div>
				<div
					className={classNames([
						styles['name'],
						styles['lower-title'],
					])}
				>
					{video.name}
				</div>
			</div>
			{hasNotification && <div className={styles['notification']} />}
		</div>
	);
};
